/**
 * Central Network Store combining elements, map, and UI state
 */
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { LatLngTuple } from 'leaflet';

import {
  PowerMeasurements,
  LineSegmentState,
} from './types/network';
import { AnyNetworkElement, NetworkElementType, Coordinates, Abgangsleitung } from '../types/network';
import { loadNetworkData } from '../services/networkDataService';
import { withErrorHandling } from './utils/storeHelpers';

// Validation functions
function isValidNetworkElementType(type: string): type is NetworkElementType {
  const validTypes = ['trafostation', 'abgangsleitung', 'kabelverteilschrank', 'hausanschluss', 'muffe'];
  return validTypes.includes(type.toLowerCase());
}

function validateAndTransformElement(element: unknown): AnyNetworkElement | null {
  if (!element || typeof element !== 'object') return null;
  
  const el = element as any;
  
  if (!el.type || !el.id) return null;
  if (!el.name && (el.type !== 'hausanschluss' || (!el.street && !el.city))) return null;
  
  // Normalize type to lowercase
  el.type = el.type.toLowerCase();
  
  if (!isValidNetworkElementType(el.type)) return null;
  
  // Ensure basic required fields
  el.id = el.id || crypto.randomUUID();
  if (el.type !== 'hausanschluss' || (!el.name && !el.street && !el.city)) {
    el.name = el.name || `New ${el.type}`;
  }
  el.description = el.description || el.name || '';
  el.metadata = el.metadata || {};
  el.switchedOn = el.switchedOn ?? true;
  
  // Handle coordinates
  if (el.type !== 'abgangsleitung') {
    if (!el.coordinates) {
      el.coordinates = { lat: 0, lng: 0 };
    } else if (Array.isArray(el.coordinates)) {
      el.coordinates = {
        lat: el.coordinates[1],
        lng: el.coordinates[0]
      };
    }
  }
  
  // Type-specific validation and defaults
  switch (el.type) {
    case 'hausanschluss':
      el.smgws = el.smgws || [];
      el.technischeRessourcen = el.technischeRessourcen || [];
      el.steuerboxes = el.steuerboxes || [];
      el.loadProfile = el.loadProfile ? el.loadProfile.toUpperCase() : 'H0';
      el.connectionKind = el.connectionKind || 'household';
      el.annualConsumption = el.annualConsumption || 3600;
      el.connectionPower = el.connectionPower || 44;
      el.street = el.street || null;
      el.houseNumber = el.houseNumber || null;
      el.city = el.city || null;
      if (el.connectedLine) {
        el.connectedLine.isEnabled = el.connectedLine.isEnabled ?? true;
        el.connectedLine.totalPower = el.connectedLine.totalPower ?? 0;
      }
      break;
      
    case 'trafostation':
      el.voltage = el.voltage || { primary: 20000, secondary: 400 };
      el.capacity = el.capacity || 630;
      el.fuses = el.fuses || {};
      el.connectedLines = el.connectedLines || [];
      el.powerKW = el.powerKW ?? null;
      el.currentA = el.currentA ?? null;
      el.loadingPercent = el.loadingPercent ?? null;
      el.connectedLines = el.connectedLines.map((line: any) => ({
        id: line.id,
        crossSection: line.crossSection,
        MeLoID: line.MeLoID || line.meLoId || '',
        isEnabled: line.isEnabled ?? true,
        cableSegments: line.cableSegments || [{ id: '', index: 0, length: 0 }]
      }));
      break;
      
    case 'kabelverteilschrank':
      el.fuses = el.fuses || {};
      el.connectedLines = el.connectedLines || [];
      el.connectedLines = el.connectedLines.map((line: any) => ({
        id: line.id || '',
        crossSection: line.crossSection || 150,
        MeLoID: line.MeLoID || line.meLoId || '',
        isEnabled: line.isEnabled ?? true,
        cableSegments: line.cableSegments || [{ id: '', index: 0, length: 0 }],
        metadata: line.metadata || {}
      }));
      break;
      
    case 'abgangsleitung':
      el.waypoints = Array.isArray(el.waypoints) ? el.waypoints.filter((wp: any) => 
        wp && typeof wp === 'object' && 
        typeof wp.lat === 'number' && !isNaN(wp.lat) &&
        typeof wp.lng === 'number' && !isNaN(wp.lng)
      ) : [];
      el.totalLength = el.totalLength || 0;
      el.segments = Array.isArray(el.segments) ? el.segments.map((segment: any) => ({
        ...segment,
        startCoordinate: segment.startCoordinate || { lat: 0, lng: 0 },
        endCoordinate: segment.endCoordinate || { lat: 0, lng: 0 },
        waypoints: Array.isArray(segment.waypoints) ? segment.waypoints : [],
        length: segment.length || 0,
        loadingPercent: segment.loadingPercent ?? null,
        currentA: segment.currentA ?? null,
        currentFromA: segment.currentFromA ?? null,
        currentToA: segment.currentToA ?? null,
        powerKW: segment.powerKW ?? null,
        powerKVAR: segment.powerKVAR ?? null,
        powerKVA: segment.powerKVA ?? null,
        fromBusVoltagePu: segment.fromBusVoltagePu ?? null,
        toBusVoltagePu: segment.toBusVoltagePu ?? null,
        fromBusVoltageKv: segment.fromBusVoltageKv ?? null,
        toBusVoltageKv: segment.toBusVoltageKv ?? null
      })) : [];
      el.switches = el.switches || [];
      el.isEnabled = el.isEnabled ?? true;
      el.cableType = el.cableType || 'NAYY';
      el.crossSection = el.crossSection || 150;
      break;
      
    case 'muffe':
      el.connectedLines = Array.isArray(el.connectedLines) ? el.connectedLines.map((line: any) => ({
        id: line.id || '',
        crossSection: line.crossSection || 150,
        meLoId: line.meLoId || '',
        isEnabled: line.isEnabled ?? true,
        cableSegments: line.cableSegments || [{ id: '', index: 0, length: 0 }],
        MeLoID: line.MeLoID || line.meLoId || '',
        metadata: line.metadata || {}
      })) : [];
      break;
  }
  
  return el as AnyNetworkElement;
}

function validateConnections(elements: AnyNetworkElement[]): AnyNetworkElement[] {
  const elementIds = new Set(elements.map(e => e.id));
  
  return elements.map(e => {
    const element = { ...e };
    
    if (element.type === 'muffe') {
      if (element.connectedLines) {
        element.connectedLines = element.connectedLines.filter(connectedLine => 
          elementIds.has(connectedLine.id)
        );
      }
    } else if (element.type === 'hausanschluss') {
      if (element.connectedLine?.id && !elementIds.has(element.connectedLine.id)) {
        element.connectedLine = undefined;
      }
    } else if (element.type === 'abgangsleitung') {
      if (element.sourceId && !elementIds.has(element.sourceId)) {
        element.sourceId = undefined;
      }
      if (element.targetId && !elementIds.has(element.targetId)) {
        element.targetId = undefined;
      }
    }
    
    return element;
  });
}

// Combined Network Store
interface NetworkStoreState {
  // Elements state
  elements: AnyNetworkElement[];
  segments: Record<string, LineSegmentState>;
  powerMeasurements: PowerMeasurements;
  powerUpdateCounter: number;
  
  // Map state
  mapPosition: {
    center: LatLngTuple;
    zoom: number;
  };
  
  // UI state
  selectedElement: AnyNetworkElement | null;
  selectedElementType: NetworkElementType | null;
  isDragging: boolean;
  lastAction: {
    type: 'add' | 'remove' | 'connect' | 'disconnect' | 'power' | 'strang';
    elementId: string;
  } | null;
  visibleElementTypes: Set<NetworkElementType>;
  mapCenter: Coordinates | null;
  mapZoom: number | null;
  searchResults: AnyNetworkElement[];
  highlightedElementId: string | null;
  viewMode: 'map' | 'grid' | 'settings' | 'overload' | 'documentation';
  
  // Common state
  isLoading: boolean;
  error: string | null;
  loadingMessage: string;
  lastUpdated: number | null;
}

interface NetworkStoreActions {
  // Element actions
  loadElements: () => Promise<void>;
  addElement: (element: AnyNetworkElement) => Promise<void>;
  removeElement: (id: string) => Promise<void>;
  updateElement: (id: string, element: Partial<AnyNetworkElement>) => Promise<{success: boolean; error?: string}>;
  setElements: (elements: AnyNetworkElement[]) => void;
  getElement: (id: string) => AnyNetworkElement | undefined;
  clearElements: () => void;
  
  // Power and connection actions
  toggleAbgangsleitungConnection: (id: string) => Promise<void>;
  toggleAbgangsleitungPower: (id: string) => Promise<void>;
  getConnectedAbgangsleitungen: (elementId: string) => Abgangsleitung[];
  updatePowerMeasurements: (measurements: PowerMeasurements) => void;
  updatePowerCalculations: (calculations: any[]) => void;
  incrementPowerUpdateCounter: () => void;
  
  // Segment actions
  updateSegment: (segmentId: string, data: Partial<LineSegmentState>) => void;
  getSegment: (segmentId: string) => LineSegmentState | undefined;
  
  // Map actions
  setMapPosition: (position: { center: LatLngTuple; zoom: number }) => void;
  
  // UI actions
  setSelectedElement: (element: AnyNetworkElement | null) => void;
  setSelectedElementType: (type: NetworkElementType | null) => void;
  setIsDragging: (isDragging: boolean) => void;
  setLastAction: (type: 'add' | 'remove' | 'connect' | 'disconnect' | 'power' | 'strang', elementId: string) => void;
  toggleElementTypeVisibility: (type: NetworkElementType) => void;
  setAllElementTypesVisible: (visible: boolean) => void;
  setMapView: (center: Coordinates, zoom: number) => void;
  setSearchResults: (results: AnyNetworkElement[]) => void;
  setHighlightedElement: (elementId: string | null) => void;
  setViewMode: (mode: 'map' | 'grid' | 'settings' | 'overload' | 'documentation') => void;
  
  // Common actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setLoadingMessage: (message: string) => void;
  reset: () => void;
}

type NetworkStore = NetworkStoreState & NetworkStoreActions;

export const useNetworkStore = create<NetworkStore>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        // Initial state
        elements: [],
        segments: {},
        powerMeasurements: {},
        powerUpdateCounter: 0,
        mapPosition: {
          center: [49.013854, 12.122648] as LatLngTuple,
          zoom: 18.5
        },
        selectedElement: null,
        selectedElementType: null,
        isDragging: false,
        lastAction: null,
        visibleElementTypes: new Set(['trafostation', 'abgangsleitung', 'kabelverteilschrank', 'hausanschluss', 'muffe']),
        mapCenter: null,
        mapZoom: null,
        searchResults: [],
        highlightedElementId: null,
        viewMode: 'map' as const,
        isLoading: false,
        error: null,
        loadingMessage: 'Lade...',
        lastUpdated: null,

        // Element actions
        loadElements: async () => {
          const { setLoading, setError } = get();
          
          await withErrorHandling(
            async () => {
              const data = await loadNetworkData();
              const elements = data.elements || [];
              
              if (!Array.isArray(elements)) {
                throw new Error('Invalid network data format');
              }
              
              const transformedElements: AnyNetworkElement[] = [];
              let validationErrors = 0;
              
              elements.forEach((element) => {
                const transformed = validateAndTransformElement(element);
                if (transformed) {
                  transformedElements.push(transformed);
                } else {
                  validationErrors++;
                }
              });
              
              if (validationErrors > 0) {
                console.warn(`[NetworkStore] ${validationErrors} elements failed validation`);
              }
              
              const validatedElements = validateConnections(transformedElements);
              
              set((state) => {
                state.elements = validatedElements;
                state.lastUpdated = Date.now();
              });
            },
            setError,
            setLoading
          );
        },

        addElement: async (element: AnyNetworkElement) => {
          const validatedElement = validateAndTransformElement(element);
          if (!validatedElement) {
            console.error('[NetworkStore] Failed to validate element:', element);
            return;
          }

          set((state) => {
            state.elements.push(validatedElement);
            state.lastUpdated = Date.now();
          });

        },

        removeElement: async (id: string) => {
          set((state) => {
            state.elements = state.elements.filter((element) => element.id !== id);
            state.elements = validateConnections(state.elements);
            state.lastUpdated = Date.now();
          });
        },

        updateElement: async (id: string, updatedElement: Partial<AnyNetworkElement>) => {
          const currentState = get();
          const existingElement = currentState.elements.find(el => el.id === id);
          
          if (!existingElement) {
            return { success: false, error: 'Element not found' };
          }

          // Check if update is actually needed
          const hasChanges = Object.entries(updatedElement).some(([key, value]) =>
            existingElement[key as keyof AnyNetworkElement] !== value
          );

          if (!hasChanges) {
            return { success: true };
          }

          const updated = { ...existingElement, ...updatedElement };
          const validated = validateAndTransformElement(updated);
          
          if (!validated) {
            return { success: false, error: 'Validation failed' };
          }

          set((state) => {
            const index = state.elements.findIndex(el => el.id === id);
            if (index !== -1) {
              state.elements[index] = validated;
              state.elements = validateConnections(state.elements);
              state.lastUpdated = Date.now();
            }
          });

          return { success: true };
        },

        setElements: (elements: AnyNetworkElement[]) => {
          set((state) => {
            state.elements = elements;
            state.lastUpdated = Date.now();
          });
        },

        getElement: (id: string) => {
          return get().elements.find((element) => element.id === id);
        },

        clearElements: () => {
          set((state) => {
            state.elements = [];
            state.segments = {};
            state.powerMeasurements = {};
            state.lastUpdated = Date.now();
          });
        },

        // Power and connection actions
        toggleAbgangsleitungConnection: async (id: string) => {
          set((state) => {
            const element = state.elements.find(el => el.id === id && el.type === 'abgangsleitung');
            if (element) {
              element.switchedOn = !element.switchedOn;
              state.lastUpdated = Date.now();
            }
          });
        },

        toggleAbgangsleitungPower: async (id: string) => {
          set((state) => {
            const element = state.elements.find(el => el.id === id && el.type === 'abgangsleitung');
            if (element) {
              element.switchedOn = !element.switchedOn;
              state.lastUpdated = Date.now();
            }
          });
        },

        getConnectedAbgangsleitungen: (elementId: string) => {
          const elements = get().elements;
          return elements.filter(
            (el): el is Abgangsleitung =>
              el.type === 'abgangsleitung' &&
              (el.sourceId === elementId || el.targetId === elementId)
          );
        },

        updatePowerMeasurements: (measurements: PowerMeasurements) => {
          set((state) => {
            Object.assign(state.powerMeasurements, measurements);
            state.powerUpdateCounter += 1;
            state.lastUpdated = Date.now();
          });
        },

        updatePowerCalculations: (_calculations: any[]) => {
        },

        incrementPowerUpdateCounter: () => {
          set((state) => {
            state.powerUpdateCounter += 1;
          });
        },

        // Segment actions
        updateSegment: (segmentId: string, data: Partial<LineSegmentState>) => {
          set((state) => {
            const currentSegment = state.segments[segmentId];
            
            // Skip update if data hasn't changed
            if (currentSegment && Object.entries(data).every(([key, value]) =>
              currentSegment[key as keyof LineSegmentState] === value
            )) {
              return;
            }

            state.segments[segmentId] = {
              ...currentSegment,
              ...data
            };
            state.lastUpdated = Date.now();
          });
        },

        getSegment: (segmentId: string) => {
          return get().segments[segmentId];
        },

        // Map actions
        setMapPosition: (position: { center: LatLngTuple; zoom: number }) => {
          set((state) => {
            const boundedZoom = Math.min(Math.max(position.zoom, 8), 19);
            state.mapPosition = {
              ...position,
              zoom: boundedZoom
            };
          });
        },

        // UI actions
        setSelectedElement: (element: AnyNetworkElement | null) => {
          set((state) => {
            state.selectedElement = element;
          });
        },

        setSelectedElementType: (type: NetworkElementType | null) => {
          set((state) => {
            state.selectedElementType = type;
          });
        },

        setIsDragging: (isDragging: boolean) => {
          set((state) => {
            state.isDragging = isDragging;
          });
        },

        setLastAction: (type, elementId) => {
          set((state) => {
            state.lastAction = { type, elementId };
          });
        },

        toggleElementTypeVisibility: (type: NetworkElementType) => {
          set((state) => {
            if (state.visibleElementTypes.has(type)) {
              state.visibleElementTypes.delete(type);
            } else {
              state.visibleElementTypes.add(type);
            }
          });
        },

        setAllElementTypesVisible: (visible: boolean) => {
          set((state) => {
            state.visibleElementTypes = visible 
              ? new Set(['trafostation', 'abgangsleitung', 'kabelverteilschrank', 'hausanschluss', 'muffe'])
              : new Set();
          });
        },

        setMapView: (center: Coordinates, zoom: number) => {
          set((state) => {
            state.mapCenter = center;
            state.mapZoom = zoom;
          });
        },

        setSearchResults: (results: AnyNetworkElement[]) => {
          set((state) => {
            state.searchResults = results;
          });
        },

        setHighlightedElement: (elementId: string | null) => {
          set((state) => {
            state.highlightedElementId = elementId;
          });
        },

        setViewMode: (mode) => {
          set((state) => {
            state.viewMode = mode;
          });
        },

        // Common actions
        setLoading: (loading: boolean) => {
          set((state) => {
            state.isLoading = loading;
            if (loading) {
              state.error = null;
            }
          });
        },

        setError: (error: string | null) => {
          set((state) => {
            state.error = error;
            state.isLoading = false;
          });
        },

        clearError: () => {
          set((state) => {
            state.error = null;
          });
        },

        setLoadingMessage: (message: string) => {
          set((state) => {
            state.loadingMessage = message;
          });
        },

        reset: () => {
          set((state) => {
            state.elements = [];
            state.segments = {};
            state.powerMeasurements = {};
            state.powerUpdateCounter = 0;
            state.selectedElement = null;
            state.selectedElementType = null;
            state.isDragging = false;
            state.lastAction = null;
            state.searchResults = [];
            state.highlightedElementId = null;
            state.isLoading = false;
            state.error = null;
            state.lastUpdated = null;
          });
        },
      }))
    ),
    { name: 'network-store' }
  )
);

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).getNetworkStore = () => useNetworkStore.getState();
}
